import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { STORAGE_KEYS } from 'constants/storageKeys';
import storeAdminAuthService from 'services/admin/storeAdminAuthService';
import { tenantStorage } from 'utils/tenantStorage';
import { StoreAdminLoginRequest } from 'types/admin';

interface StoreAdminContextValue {
  isAdminAuthenticated: boolean;
  displayName: string | null;
  login: (data: StoreAdminLoginRequest) => Promise<void>;
  acceptHandoff: (token: string, displayName?: string) => void;
  logout: () => void;
}

const StoreAdminContext = createContext<StoreAdminContextValue | undefined>(undefined);

export const StoreAdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(
    () => !!tenantStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN),
  );
  const [displayName, setDisplayName] = useState<string | null>(
    () => tenantStorage.getItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME),
  );

  const login = useCallback(async (data: StoreAdminLoginRequest) => {
    const session = await storeAdminAuthService.login(data);
    setIsAdminAuthenticated(true);
    setDisplayName(session.displayName ?? data.username);
  }, []);

  const acceptHandoff = useCallback((token: string, name?: string) => {
    storeAdminAuthService.acceptHandoffToken(token, name);
    setIsAdminAuthenticated(true);
    setDisplayName(name ?? tenantStorage.getItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME));
  }, []);

  const logout = useCallback(() => {
    storeAdminAuthService.clearSession();
    setIsAdminAuthenticated(false);
    setDisplayName(null);
  }, []);

  const value = useMemo(
    () => ({ isAdminAuthenticated, displayName, login, acceptHandoff, logout }),
    [isAdminAuthenticated, displayName, login, acceptHandoff, logout],
  );

  return <StoreAdminContext.Provider value={value}>{children}</StoreAdminContext.Provider>;
};

export const useStoreAdminContext = () => {
  const ctx = useContext(StoreAdminContext);
  if (!ctx) throw new Error('useStoreAdminContext debe usarse dentro de StoreAdminProvider');
  return ctx;
};
